import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { eur } from "../lib/format";
import { ReorderableGallery } from "../components/ReorderableGallery";
import { PublicPreviewModal, CompletionChecklist } from "../components/PublicPreviewModal";
import { Eye } from "lucide-react";
import * as apiClient from "../api";

const PROD_LABELS = { nome: "Nome", descrizione: "Descrizione", prezzo: "Prezzo", cover: "Copertina", galleria: "Galleria (min 2 foto)", categoria: "Categoria" };
function prodChecklist(p) {
  const imgs = p.images || [];
  const c = { nome: !!p.name, descrizione: !!(p.description || p.short_description), prezzo: (p.price || 0) > 0, cover: imgs.length >= 1, galleria: imgs.length >= 2, categoria: !!p.category };
  return { c, pct: Math.round(Object.values(c).filter(Boolean).length / Object.keys(c).length * 100) };
}

const SECTIONS = [
  { key: "attivita", label: "Attività", fields: [
    ["legal_name", "Ragione sociale"], ["brand_name", "Nome commerciale"], ["subject_type", "Tipologia soggetto"],
    ["vat", "Partita IVA"], ["fiscal_code", "Codice Fiscale"], ["legal_address", "Indirizzo sede legale"],
    ["zip", "CAP"], ["city", "Comune"], ["province", "Provincia"], ["country", "Nazione"],
    ["operational_address", "Sede operativa"], ["website", "Sito web"],
  ]},
  { key: "contatti", label: "Contatti", fields: [
    ["company_phone", "Telefono aziendale"], ["mobile", "Cellulare / diretto"], ["whatsapp", "WhatsApp"],
    ["email", "Email"], ["pec", "PEC"], ["sdi", "Codice SDI"], ["rea", "REA"],
  ]},
  { key: "referente", label: "Referente", fields: [
    ["referent_name", "Nome"], ["referent_surname", "Cognome"], ["referent_role", "Ruolo"],
    ["referent_phone", "Telefono"], ["referent_email", "Email"],
  ]},
  { key: "banca", label: "Banca (riservato)", fields: [
    ["account_holder", "Intestatario conto"], ["iban", "IBAN"], ["bic", "BIC/SWIFT"],
    ["bank_name", "Banca"], ["bank_notes", "Note amministrative"],
  ]},
];

export function ProfileTab() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["opProfile"], queryFn: apiClient.getOperatorProfile });
  const { data: docs = [] } = useQuery({ queryKey: ["opDocs"], queryFn: apiClient.getOperatorDocuments });
  const [form, setForm] = useState({});
  useEffect(() => { if (data?.operator) setForm(data.operator); }, [data]);

  const save = useMutation({
    mutationFn: apiClient.updateOperatorProfile,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["opProfile"] }); toast.success("Profilo salvato"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore nel salvataggio"),
  });
  const addDoc = useMutation({
    mutationFn: apiClient.addOperatorDocument,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["opDocs"] }); toast.success("Documento aggiunto"); },
  });

  if (isLoading) return <p className="text-slate-400">Caricamento...</p>;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = () => {
    const payload = {};
    SECTIONS.forEach((s) => s.fields.forEach(([k]) => { if (form[k] !== undefined) payload[k] = form[k]; }));
    save.mutate(payload);
  };

  return (
    <div className="space-y-6" data-testid="operator-profile">
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-2">
          <span className="font-heading font-semibold text-slate-900">Profilo completato</span>
          <span className="font-bold text-primary" data-testid="profile-completion">{data?.completion ?? 0}%</span>
        </div>
        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full bg-primary transition-all" style={{ width: `${data?.completion ?? 0}%` }} />
        </div>
        {data?.missing?.length > 0 && <p className="mt-2 text-xs text-slate-400">Mancano: {data.missing.join(", ")}</p>}
      </div>

      {SECTIONS.map((s) => (
        <div key={s.key} className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-heading font-semibold text-slate-900 mb-4">{s.label}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {s.fields.map(([k, label]) => (
              <label key={k} className="block">
                <span className="text-xs font-medium text-slate-500">{label}</span>
                <input data-testid={`profile-field-${k}`} value={form[k] || ""} onChange={(e) => set(k, e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-secondary" />
              </label>
            ))}
          </div>
        </div>
      ))}

      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="font-heading font-semibold text-slate-900 mb-4">Documenti</h3>
        <div className="space-y-2 mb-4">
          {docs.length === 0 ? <p className="text-sm text-slate-400">Nessun documento caricato.</p> : docs.map((d) => (
            <div key={d.id} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2 text-sm">
              <span>{d.name} <span className="text-xs text-slate-400">({d.doc_type})</span></span>
              <span className={`text-xs font-semibold ${d.status === "VERIFICATO" ? "text-emerald-600" : d.status === "RIFIUTATO" ? "text-red-500" : "text-amber-500"}`}>{d.status}</span>
            </div>
          ))}
        </div>
        <DocForm onAdd={(d) => addDoc.mutate(d)} />
      </div>

      <button onClick={submit} disabled={save.isPending} data-testid="profile-save-btn"
        className="rounded-full bg-primary text-white font-semibold px-8 py-3 hover:bg-primary/90 transition-colors">
        {save.isPending ? "Salvataggio..." : "Salva profilo"}
      </button>
    </div>
  );
}

function DocForm({ onAdd }) {
  const [d, setD] = useState({ doc_type: "visura", name: "", file_url: "", expiry: "", notes: "" });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
      <select value={d.doc_type} onChange={(e) => setD({ ...d, doc_type: e.target.value })} className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
        {["visura", "identita", "licenza", "assicurazione", "certificazione", "sanitario", "altro"].map((x) => <option key={x} value={x}>{x}</option>)}
      </select>
      <input placeholder="Nome documento" value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
      <input placeholder="URL file" value={d.file_url} onChange={(e) => setD({ ...d, file_url: e.target.value })} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
      <button onClick={() => { if (d.name && d.file_url) onAdd(d); }} className="rounded-lg bg-slate-900 text-white text-sm font-semibold px-4 py-2">Aggiungi</button>
    </div>
  );
}

const EMPTY_PRODUCT = { name: "", category: "food", destination: "gargano", short_description: "", description: "",
  price: 0, discount_price: null, stock: 0, sku: "", is_food: false, allergens: "", ingredients: "",
  conservation: "", origin: "", shipping_available: true, pickup_available: true, active: true, images: [] };

export function ProductsTab({ operatorId }) {
  const qc = useQueryClient();
  const { data: products = [] } = useQuery({ queryKey: ["opProducts"], queryFn: apiClient.getOperatorProducts });
  const { data: cats = [] } = useQuery({ queryKey: ["productCategories"], queryFn: apiClient.getProductCategories });
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(false);

  const mut = useMutation({
    mutationFn: (p) => p.id ? apiClient.updateOperatorProduct(p.id, p) : apiClient.createOperatorProduct(p),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["opProducts"] }); setEditing(null); toast.success("Prodotto salvato"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  if (editing) {
    const p = editing;
    const set = (k, v) => setEditing({ ...p, [k]: v });
    const { c, pct } = prodChecklist(p);
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4 max-w-2xl" data-testid="product-form">
        <h3 className="font-heading font-semibold text-slate-900">{p.id ? "Modifica prodotto" : "Nuovo prodotto"}</h3>
        <CompletionChecklist percent={pct} checklist={c} labels={PROD_LABELS} testid="product-completion" />
        <input data-testid="product-name" placeholder="Nome prodotto" value={p.name} onChange={(e) => set("name", e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        <div className="grid grid-cols-2 gap-3">
          <select value={p.destination} onChange={(e) => set("destination", e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
            <option value="gargano">Gargano</option><option value="dolomiti">Dolomiti</option>
          </select>
          <select value={p.category} onChange={(e) => set("category", e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
            {cats.map((c2) => <option key={c2.slug} value={c2.slug}>{c2.name}</option>)}
          </select>
          <input type="number" placeholder="Prezzo €" value={p.price} onChange={(e) => set("price", parseFloat(e.target.value) || 0)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          <input type="number" placeholder="Stock" value={p.stock} onChange={(e) => set("stock", parseInt(e.target.value) || 0)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        </div>
        <ReorderableGallery testid="product-gallery" value={p.images} onChange={(v) => set("images", v)} />
        <textarea placeholder="Descrizione breve" value={p.short_description} onChange={(e) => set("short_description", e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" rows={2} />
        <textarea placeholder="Descrizione completa" value={p.description} onChange={(e) => set("description", e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" rows={3} />
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={p.is_food} onChange={(e) => set("is_food", e.target.checked)} /> Prodotto alimentare</label>
        {p.is_food && (
          <div className="space-y-2">
            <input placeholder="Ingredienti" value={p.ingredients} onChange={(e) => set("ingredients", e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <input placeholder="Allergeni" value={p.allergens} onChange={(e) => set("allergens", e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <input placeholder="Conservazione" value={p.conservation} onChange={(e) => set("conservation", e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          </div>
        )}
        <div className="flex gap-3 items-center">
          <button data-testid="product-save" onClick={() => mut.mutate({ ...p, operator_id: operatorId })} className="rounded-full bg-primary text-white font-semibold px-6 py-2.5">Salva</button>
          <button onClick={() => setEditing(null)} className="rounded-full bg-slate-100 text-slate-600 font-semibold px-6 py-2.5">Annulla</button>
          <button onClick={() => setPreview(true)} data-testid="product-preview-btn" className="ml-auto rounded-full border px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2"><Eye className="h-4 w-4" /> Anteprima</button>
        </div>
        {preview && <PublicPreviewModal data={p} kind="product" publicUrl={p.slug && p.active ? `/shop/${p.slug}` : null} onClose={() => setPreview(false)} />}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="operator-products">
      <button data-testid="new-product-btn" onClick={() => setEditing({ ...EMPTY_PRODUCT })} className="rounded-full bg-primary text-white font-semibold px-6 py-2.5">+ Nuovo prodotto</button>
      {products.length === 0 ? <p className="text-sm text-slate-400">Nessun prodotto. Aggiungi il primo!</p> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map((p) => (
            <div key={p.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <img src={p.images?.[0]} alt={p.name} className="h-32 w-full object-cover" />
              <div className="p-4">
                <div className="font-semibold text-slate-800 text-sm">{p.name}</div>
                <div className="text-xs text-slate-400">{p.destination} · {p.category} · {eur(p.price)} · stock {p.stock}</div>
                <button onClick={() => setEditing(p)} className="mt-2 text-xs font-semibold text-primary">Modifica</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
